import {Fragment, useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {
    createAdminJobVacancy,
    createAdminPrice,
    createAdminStaffMember,
    deactivateAdminJobVacancy,
    deactivateAdminPrice,
    deactivateAdminStaffMember,
    getAdminJobVacancies,
    getAdminPrices,
    getAdminServiceRequests,
    getAdminStaffMembers,
    getAdminUsers,
    updateAdminJobVacancy,
    updateAdminPrice,
    updateAdminServiceRequestFields,
    updateAdminServiceRequestStatus,
    updateAdminStaffMember,
    updateAdminUserRole,
} from "../../api/client";

const requestStatuses = ["NEW", "IN_PROGRESS", "NEEDS_CLARIFICATION", "ACCEPTED", "IN_WORK", "COMPLETED", "CANCELLED"];
const userRoles = ["CLIENT", "ADMIN"];
const userPreviewLimit = 5;
const requestPreviewLimit = 5;
const userPageSizeOptions = [5, 10, 15];
const requestPageSizeOptions = [5, 10, 15];
const adminFavoriteUsersStorageKey = "truckCenterAdminFavoriteUsers";
const userSortOptions = [
    {value: "name", label: "По ФИО"},
    {value: "phone", label: "По телефону"},
    {value: "role", label: "По роли"},
    {value: "requests", label: "По заявкам"},
];
const userRequestSortOptions = [
    {value: "requestsCount", label: "По кол-ву заявок"},
    {value: "requestsTotalPrice", label: "По сумме заявок"},
    {value: "requestsMaxPrice", label: "По дорогой заявке"},
    {value: "latestRequest", label: "По последней заявке"},
    {value: "requestsCompleteness", label: "По заполненности заявок"},
];
const requestStatusLabels = {
    NEW: "Новая",
    IN_PROGRESS: "В обработке",
    NEEDS_CLARIFICATION: "Ожидает уточнения",
    ACCEPTED: "Принята",
    IN_WORK: "В работе",
    COMPLETED: "Выполнена",
    CANCELLED: "Отменена",
};

function formatAdminDate(value) {
    if (!value) {
        return "—";
    }

    return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(value));
}

function formatRubles(value) {
    if (!value) {
        return "—";
    }

    return new Intl.NumberFormat("ru-RU").format(value) + " ₽";
}

function readFavoriteUsers() {
    try {
        const value = localStorage.getItem(adminFavoriteUsersStorageKey);
        const ids = value ? JSON.parse(value) : [];
        return Array.isArray(ids) ? ids : [];
    } catch {
        return [];
    }
}

const emptyPriceForm = {
    title: "",
    price: "",
    description: "",
    sortOrder: 0,
    active: true,
};
const emptyStaffForm = {
    name: "",
    role: "",
    description: "",
    sortOrder: 0,
    active: true,
};
const emptyVacancyForm = {
    title: "",
    description: "",
    sortOrder: 0,
    active: true,
};

function Admin({auth}) {
    const pendingRequestFocusRef = useRef("");
    const focusedRequestRowRef = useRef(null);
    const token = auth?.token;
    const isAdmin = auth?.user?.role === "ADMIN";
    const [users, setUsers] = useState([]);
    const [serviceRequests, setServiceRequests] = useState([]);
    const [prices, setPrices] = useState([]);
    const [staffMembers, setStaffMembers] = useState([]);
    const [jobVacancies, setJobVacancies] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [userSearch, setUserSearch] = useState("");
    const [userRoleFilter, setUserRoleFilter] = useState("");
    const [userSort, setUserSort] = useState("name");
    const [userRequestSort, setUserRequestSort] = useState("requestsCount");
    const [prioritizeFavorites, setPrioritizeFavorites] = useState(false);
    const [userPageSize, setUserPageSize] = useState(userPreviewLimit);
    const [visibleUsersLimit, setVisibleUsersLimit] = useState(userPreviewLimit);
    const [expandedUserId, setExpandedUserId] = useState("");
    const [expandedUserRequestsId, setExpandedUserRequestsId] = useState("");
    const [favoriteUserIds, setFavoriteUserIds] = useState(() => readFavoriteUsers());
    const [isUsersListAnimating, setIsUsersListAnimating] = useState(false);
    const [requestSearch, setRequestSearch] = useState("");
    const [prioritizePinnedRequests, setPrioritizePinnedRequests] = useState(false);
    const [requestPageSize, setRequestPageSize] = useState(requestPreviewLimit);
    const [visibleRequestsLimit, setVisibleRequestsLimit] = useState(requestPreviewLimit);
    const [isRequestsListAnimating, setIsRequestsListAnimating] = useState(false);
    const [focusedRequestId, setFocusedRequestId] = useState("");
    const [expandedRequestId, setExpandedRequestId] = useState("");
    const [requestNotes, setRequestNotes] = useState({});
    const [priceForm, setPriceForm] = useState(emptyPriceForm);
    const [editingPriceId, setEditingPriceId] = useState("");
    const [staffForm, setStaffForm] = useState(emptyStaffForm);
    const [editingStaffId, setEditingStaffId] = useState("");
    const [vacancyForm, setVacancyForm] = useState(emptyVacancyForm);
    const [editingVacancyId, setEditingVacancyId] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [pendingAction, setPendingAction] = useState("");
    const [actionMessage, setActionMessage] = useState("");
    const [error, setError] = useState("");
    const [vacancyError, setVacancyError] = useState("");
    const adminUsersCount = users.filter((user) => user.role === "ADMIN").length;
    const clientUsersCount = users.filter((user) => user.role === "CLIENT").length;
    const visibleServiceRequests = serviceRequests
        .filter((request) => {
            const search = requestSearch.trim().toLowerCase();
            if (!search) {
                return true;
            }

            return [
                request.id,
                request.customerName,
                request.phone,
                request.vehicle,
                request.serviceType,
                request.message,
                request.status,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(search);
        })
        .sort((leftRequest, rightRequest) => {
            if (prioritizePinnedRequests && leftRequest.pinned !== rightRequest.pinned) {
                return leftRequest.pinned ? -1 : 1;
            }

            return new Date(rightRequest.createdAt || 0).getTime() - new Date(leftRequest.createdAt || 0).getTime();
        });
    const findUserRequests = (phone) => serviceRequests
        .filter((request) => request.phone === phone)
        .sort((leftRequest, rightRequest) => (
            new Date(rightRequest.createdAt || 0).getTime() - new Date(leftRequest.createdAt || 0).getTime()
        ));
    const estimateRequestPrice = (request) => {
        const serviceType = request.serviceType?.trim().toLowerCase();
        if (!serviceType) {
            return 0;
        }

        const matchedPrice = prices.find((price) => (
            price.title?.trim().toLowerCase() === serviceType
            || serviceType.includes(price.title?.trim().toLowerCase())
            || price.title?.trim().toLowerCase().includes(serviceType)
        ));
        const rawPrice = matchedPrice?.price || "";
        const numericPrice = rawPrice.match(/\d[\d\s]*/)?.[0]?.replace(/\s/g, "");

        return numericPrice ? Number(numericPrice) : 0;
    };
    const getUserRequestMetrics = (user) => {
        const userRequests = findUserRequests(user.phone);
        const estimatedPrices = userRequests.map(estimateRequestPrice);
        const requestsTotalPrice = estimatedPrices.reduce((sum, price) => sum + price, 0);
        const requestsMaxPrice = estimatedPrices.length ? Math.max(...estimatedPrices) : 0;
        const latestRequestTime = userRequests.length
            ? new Date(userRequests[0].createdAt || 0).getTime()
            : 0;
        const requestsCompleteness = userRequests.reduce((score, request) => (
            score
            + (request.vehicle ? 1 : 0)
            + (request.serviceType ? 1 : 0)
            + (request.message ? 1 : 0)
        ), 0);

        return {
            requestsCount: userRequests.length,
            requestsTotalPrice,
            requestsMaxPrice,
            latestRequestTime,
            requestsCompleteness,
        };
    };
    const visibleUsers = users
        .filter((user) => {
            const search = userSearch.trim().toLowerCase();
            const matchesSearch = !search
                || `${user.id} ${user.fullName} ${user.phone} ${user.role}`.toLowerCase().includes(search);
            const matchesRole = !userRoleFilter || user.role === userRoleFilter;

            return matchesSearch && matchesRole;
        })
        .sort((leftUser, rightUser) => {
            const leftMetrics = getUserRequestMetrics(leftUser);
            const rightMetrics = getUserRequestMetrics(rightUser);
            const leftFavorite = favoriteUserIds.includes(leftUser.id) ? 1 : 0;
            const rightFavorite = favoriteUserIds.includes(rightUser.id) ? 1 : 0;

            if (prioritizeFavorites && leftFavorite !== rightFavorite) {
                return rightFavorite - leftFavorite;
            }

            if (userSort === "requests" && userRequestSort === "requestsCount") {
                return rightMetrics.requestsCount - leftMetrics.requestsCount || leftUser.fullName.localeCompare(rightUser.fullName);
            }

            if (userSort === "requests" && userRequestSort === "requestsTotalPrice") {
                return rightMetrics.requestsTotalPrice - leftMetrics.requestsTotalPrice || leftUser.fullName.localeCompare(rightUser.fullName);
            }

            if (userSort === "requests" && userRequestSort === "requestsMaxPrice") {
                return rightMetrics.requestsMaxPrice - leftMetrics.requestsMaxPrice || leftUser.fullName.localeCompare(rightUser.fullName);
            }

            if (userSort === "requests" && userRequestSort === "latestRequest") {
                return rightMetrics.latestRequestTime - leftMetrics.latestRequestTime || leftUser.fullName.localeCompare(rightUser.fullName);
            }

            if (userSort === "requests" && userRequestSort === "requestsCompleteness") {
                return rightMetrics.requestsCompleteness - leftMetrics.requestsCompleteness || leftUser.fullName.localeCompare(rightUser.fullName);
            }

            if (userSort === "phone") {
                return leftUser.phone.localeCompare(rightUser.phone);
            }

            if (userSort === "role") {
                return leftUser.role.localeCompare(rightUser.role) || leftUser.fullName.localeCompare(rightUser.fullName);
            }

            return leftUser.fullName.localeCompare(rightUser.fullName);
        });
    const isUserSearchActive = Boolean(userSearch.trim());
    const hasUserFilters = isUserSearchActive || Boolean(userRoleFilter);
    const userPageStep = userPageSize === "all" ? visibleUsers.length : Number(userPageSize);
    const effectiveUserLimit = userPageSize === "all" ? visibleUsers.length : visibleUsersLimit;
    const displayedUsers = visibleUsers.slice(0, effectiveUserLimit);
    const hiddenUsersCount = Math.max(visibleUsers.length - displayedUsers.length, 0);
    const nextUsersCount = Math.min(userPageStep, hiddenUsersCount);
    const isRequestSearchActive = Boolean(requestSearch.trim());
    const hasRequestFilters = isRequestSearchActive || Boolean(statusFilter);
    const requestPageStep = requestPageSize === "all" ? visibleServiceRequests.length : Number(requestPageSize);
    const effectiveRequestLimit = requestPageSize === "all" ? visibleServiceRequests.length : visibleRequestsLimit;
    const displayedServiceRequests = visibleServiceRequests.slice(0, effectiveRequestLimit);
    const hiddenRequestsCount = Math.max(visibleServiceRequests.length - displayedServiceRequests.length, 0);
    const nextRequestsCount = Math.min(requestPageStep, hiddenRequestsCount);
    const activePricesCount = prices.filter((price) => price.active).length;
    const activeStaffCount = staffMembers.filter((member) => member.active).length;
    const activeVacanciesCount = jobVacancies.filter((vacancy) => vacancy.active).length;

    function focusRequest(requestId) {
        pendingRequestFocusRef.current = requestId;
        setFocusedRequestId(requestId);
        setStatusFilter("");
        setRequestSearch(requestId);
    }

    function toggleFavoriteUser(userId) {
        setFavoriteUserIds((currentIds) => {
            const nextIds = currentIds.includes(userId)
                ? currentIds.filter((id) => id !== userId)
                : [...currentIds, userId];

            localStorage.setItem(adminFavoriteUsersStorageKey, JSON.stringify(nextIds));
            return nextIds;
        });
    }

    function handleShowMoreUsers() {
        setVisibleUsersLimit((currentLimit) => Math.min(
            currentLimit + userPageStep,
            visibleUsers.length
        ));
        setIsUsersListAnimating(true);

        window.setTimeout(() => {
            setIsUsersListAnimating(false);
        }, 520);
    }

    function handleShowMoreRequests() {
        setVisibleRequestsLimit((currentLimit) => Math.min(
            currentLimit + requestPageStep,
            visibleServiceRequests.length
        ));
        setIsRequestsListAnimating(true);

        window.setTimeout(() => {
            setIsRequestsListAnimating(false);
        }, 520);
    }

    function handleUserRequestNavigation(user) {
        const userRequests = findUserRequests(user.phone);

        if (userRequests.length === 0) {
            return;
        }

        if (userRequests.length === 1) {
            focusRequest(userRequests[0].id);
            return;
        }

        setExpandedUserId(user.id);
        setExpandedUserRequestsId((currentUserId) => currentUserId === user.id ? "" : user.id);
    }

    useEffect(() => {
        if (!token || !isAdmin) {
            return;
        }

        let isMounted = true;
        setIsLoading(true);
        setError("");

        Promise.allSettled([
            getAdminUsers(token),
            getAdminServiceRequests(token, statusFilter),
            getAdminPrices(token),
            getAdminStaffMembers(token),
            getAdminJobVacancies(token),
        ])
            .then(([usersResult, requestsResult, pricesResult, staffResult, vacanciesResult]) => {
                if (isMounted) {
                    const criticalResults = [usersResult, requestsResult, pricesResult, staffResult];
                    const failedCriticalResult = criticalResults.find((result) => result.status === "rejected");

                    if (failedCriticalResult) {
                        setError(failedCriticalResult.reason.message);
                    } else {
                        setError("");
                    }

                    if (usersResult.status === "fulfilled") {
                        setUsers(usersResult.value);
                    }
                    if (requestsResult.status === "fulfilled") {
                        setServiceRequests(requestsResult.value);
                    }
                    if (pricesResult.status === "fulfilled") {
                        setPrices(pricesResult.value);
                    }
                    if (staffResult.status === "fulfilled") {
                        setStaffMembers(staffResult.value);
                    }
                    if (vacanciesResult.status === "fulfilled") {
                        setJobVacancies(vacanciesResult.value);
                        setVacancyError("");
                    } else {
                        setJobVacancies([]);
                        setVacancyError(vacanciesResult.reason.message);
                    }
                }
            })
            .catch((requestError) => {
                if (isMounted) {
                    setError(requestError.message);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [token, isAdmin, statusFilter]);

    useEffect(() => {
        const pendingRequestId = pendingRequestFocusRef.current;

        if (!pendingRequestId || isLoading) {
            return;
        }

        const isRequestVisible = visibleServiceRequests.some((request) => request.id === pendingRequestId);

        if (!isRequestVisible) {
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            const rowElement = focusedRequestRowRef.current;

            if (!rowElement || pendingRequestFocusRef.current !== pendingRequestId) {
                return;
            }

            rowElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest",
            });
            rowElement.focus({preventScroll: true});
            pendingRequestFocusRef.current = "";
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [isLoading, visibleServiceRequests]);

    useEffect(() => {
        setVisibleUsersLimit(userPageSize === "all" ? Number.MAX_SAFE_INTEGER : Number(userPageSize));
    }, [prioritizeFavorites, userPageSize, userRequestSort, userRoleFilter, userSearch, userSort]);

    useEffect(() => {
        setVisibleRequestsLimit(requestPageSize === "all" ? Number.MAX_SAFE_INTEGER : Number(requestPageSize));
    }, [prioritizePinnedRequests, requestPageSize, requestSearch, statusFilter]);

    async function handleRoleChange(userId, role) {
        if (userId === auth?.user?.id) {
            setError("Нельзя изменить роль текущего администратора из этой панели.");
            return;
        }

        setError("");
        setActionMessage("");
        setPendingAction(`user-${userId}`);

        try {
            const updatedUser = await updateAdminUserRole(token, userId, role);
            setUsers((currentUsers) => currentUsers.map((user) => user.id === userId ? updatedUser : user));
            setActionMessage("Роль пользователя обновлена.");
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingAction("");
        }
    }

    async function handleStatusChange(requestId, status) {
        setError("");
        setActionMessage("");
        setPendingAction(`request-${requestId}`);

        try {
            const updatedRequest = await updateAdminServiceRequestStatus(token, requestId, status);
            setServiceRequests((currentRequests) => currentRequests.map((request) => (
                request.id === requestId ? updatedRequest : request
            )).filter((request) => !statusFilter || request.status === statusFilter));
            setActionMessage("Статус заявки обновлён.");
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingAction("");
        }
    }

    async function handleRequestAdminFieldsChange(request, fields) {
        setError("");
        setActionMessage("");
        setPendingAction(`request-fields-${request.id}`);

        try {
            const updatedRequest = await updateAdminServiceRequestFields(token, request.id, fields);
            setServiceRequests((currentRequests) => currentRequests.map((currentRequest) => (
                currentRequest.id === request.id ? updatedRequest : currentRequest
            )));
            setRequestNotes((currentNotes) => ({
                ...currentNotes,
                [request.id]: updatedRequest.adminNote || "",
            }));
            setActionMessage("Параметры заявки обновлены.");
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingAction("");
        }
    }

    function handleRequestNoteChange(requestId, value) {
        setRequestNotes((currentNotes) => ({
            ...currentNotes,
            [requestId]: value,
        }));
    }

    function handlePriceFieldChange(field, value) {
        setPriceForm((currentForm) => ({
            ...currentForm,
            [field]: value,
        }));
    }

    function handleStaffFieldChange(field, value) {
        setStaffForm((currentForm) => ({
            ...currentForm,
            [field]: value,
        }));
    }

    function handleVacancyFieldChange(field, value) {
        setVacancyForm((currentForm) => ({
            ...currentForm,
            [field]: value,
        }));
    }

    function startEditPrice(price) {
        setEditingPriceId(price.id);
        setPriceForm({
            title: price.title,
            price: price.price,
            description: price.description,
            sortOrder: price.sortOrder,
            active: price.active,
        });
    }

    function resetPriceForm() {
        setEditingPriceId("");
        setPriceForm(emptyPriceForm);
    }

    async function handlePriceSubmit(event) {
        event.preventDefault();
        setError("");
        setActionMessage("");
        setPendingAction("price-form");

        const payload = {
            ...priceForm,
            sortOrder: Number(priceForm.sortOrder) || 0,
        };

        try {
            const savedPrice = editingPriceId
                ? await updateAdminPrice(token, editingPriceId, payload)
                : await createAdminPrice(token, payload);

            setPrices((currentPrices) => {
                if (editingPriceId) {
                    return currentPrices.map((price) => price.id === editingPriceId ? savedPrice : price);
                }

                return [...currentPrices, savedPrice].sort((left, right) => left.sortOrder - right.sortOrder);
            });
            setActionMessage(editingPriceId ? "Позиция прайса обновлена." : "Позиция прайса добавлена.");
            resetPriceForm();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingAction("");
        }
    }

    async function handlePriceDeactivate(priceId) {
        if (!window.confirm("Скрыть эту позицию прайса с публичной страницы?")) {
            return;
        }

        setError("");
        setActionMessage("");
        setPendingAction(`price-${priceId}`);

        try {
            await deactivateAdminPrice(token, priceId);
            setPrices((currentPrices) => currentPrices.map((price) => (
                price.id === priceId ? {...price, active: false} : price
            )));
            setActionMessage("Позиция прайса скрыта с публичной страницы.");
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingAction("");
        }
    }

    function startEditStaff(member) {
        setEditingStaffId(member.id);
        setStaffForm({
            name: member.name,
            role: member.role,
            description: member.description,
            sortOrder: member.sortOrder,
            active: member.active,
        });
    }

    function resetStaffForm() {
        setEditingStaffId("");
        setStaffForm(emptyStaffForm);
    }

    async function handleStaffSubmit(event) {
        event.preventDefault();
        setError("");
        setActionMessage("");
        setPendingAction("staff-form");

        const payload = {
            ...staffForm,
            sortOrder: Number(staffForm.sortOrder) || 0,
        };

        try {
            const savedMember = editingStaffId
                ? await updateAdminStaffMember(token, editingStaffId, payload)
                : await createAdminStaffMember(token, payload);

            setStaffMembers((currentMembers) => {
                if (editingStaffId) {
                    return currentMembers.map((member) => member.id === editingStaffId ? savedMember : member);
                }

                return [...currentMembers, savedMember].sort((left, right) => left.sortOrder - right.sortOrder);
            });
            setActionMessage(editingStaffId ? "Сотрудник обновлён." : "Сотрудник добавлен.");
            resetStaffForm();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingAction("");
        }
    }

    async function handleStaffDeactivate(staffId) {
        if (!window.confirm("Скрыть этого сотрудника с публичной страницы?")) {
            return;
        }

        setError("");
        setActionMessage("");
        setPendingAction(`staff-${staffId}`);

        try {
            await deactivateAdminStaffMember(token, staffId);
            setStaffMembers((currentMembers) => currentMembers.map((member) => (
                member.id === staffId ? {...member, active: false} : member
            )));
            setActionMessage("Сотрудник скрыт с публичной страницы.");
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingAction("");
        }
    }

    function startEditVacancy(vacancy) {
        setEditingVacancyId(vacancy.id);
        setVacancyForm({
            title: vacancy.title,
            description: vacancy.description,
            sortOrder: vacancy.sortOrder,
            active: vacancy.active,
        });
    }

    function resetVacancyForm() {
        setEditingVacancyId("");
        setVacancyForm(emptyVacancyForm);
    }

    async function handleVacancySubmit(event) {
        event.preventDefault();
        setError("");
        setActionMessage("");
        setPendingAction("vacancy-form");

        const payload = {
            ...vacancyForm,
            sortOrder: Number(vacancyForm.sortOrder) || 0,
        };

        try {
            const savedVacancy = editingVacancyId
                ? await updateAdminJobVacancy(token, editingVacancyId, payload)
                : await createAdminJobVacancy(token, payload);

            setJobVacancies((currentVacancies) => {
                if (editingVacancyId) {
                    return currentVacancies.map((vacancy) => vacancy.id === editingVacancyId ? savedVacancy : vacancy);
                }

                return [...currentVacancies, savedVacancy].sort((left, right) => left.sortOrder - right.sortOrder);
            });
            setActionMessage(editingVacancyId ? "Вакансия обновлена." : "Вакансия добавлена.");
            resetVacancyForm();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingAction("");
        }
    }

    async function handleVacancyActiveChange(vacancy, active) {
        const confirmationText = active
            ? "Показать эту вакансию на публичной странице?"
            : "Скрыть эту вакансию с публичной страницы?";

        if (!window.confirm(confirmationText)) {
            return;
        }

        setError("");
        setActionMessage("");
        setPendingAction(`vacancy-${vacancy.id}`);

        const payload = {
            title: vacancy.title,
            description: vacancy.description,
            sortOrder: vacancy.sortOrder,
            active,
        };

        try {
            const updatedVacancy = active
                ? await updateAdminJobVacancy(token, vacancy.id, payload)
                : await deactivateAdminJobVacancy(token, vacancy.id).then(() => ({...vacancy, active: false}));

            setJobVacancies((currentVacancies) => currentVacancies.map((vacancy) => (
                vacancy.id === updatedVacancy.id ? updatedVacancy : vacancy
            )));
            setActionMessage(active ? "Вакансия показана на публичной странице." : "Вакансия скрыта с публичной страницы.");
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingAction("");
        }
    }

    if (!token) {
        return (
            <main className="admin-page">
                <section className="admin-hero">
                    <span className="admin-eyebrow">Администрирование</span>
                    <h1>Нужен вход</h1>
                    <p>Войдите в аккаунт администратора, чтобы управлять данными сайта.</p>
                    <Link className="admin-home-link" to="/login">Войти</Link>
                </section>
            </main>
        );
    }

    if (!isAdmin) {
        return (
            <main className="admin-page">
                <section className="admin-hero">
                    <span className="admin-eyebrow">Администрирование</span>
                    <h1>Недостаточно прав</h1>
                    <p>Текущий аккаунт не имеет роли ADMIN.</p>
                    <Link className="admin-home-link" to="/">На главную</Link>
                </section>
            </main>
        );
    }

    return (
        <main className="admin-page">
            <section className="admin-hero">
                <div>
                    <span className="admin-eyebrow">Панель управления</span>
                    <h1>Админка Трак-Центр 24</h1>
                    <p>Управление пользователями, заявками и справочниками, которые отдаются backend API.</p>
                </div>
                <Link className="admin-home-link" to="/">На главную</Link>
            </section>

            {error && <p className="admin-status admin-status-error">{error}</p>}
            {actionMessage && <p className="admin-status admin-status-success">{actionMessage}</p>}
            {isLoading && <p className="admin-status">Загружаем данные...</p>}

            <section className="admin-summary" aria-label="Сводка админки">
                <article>
                    <span>Пользователи</span>
                    <strong>{users.length}</strong>
                </article>
                <article>
                    <span>Заявки</span>
                    <strong>{serviceRequests.length}</strong>
                </article>
                <article>
                    <span>Активный прайс</span>
                    <strong>{activePricesCount}</strong>
                </article>
                <article>
                    <span>Активный персонал</span>
                    <strong>{activeStaffCount}</strong>
                </article>
                <article>
                    <span>Активные вакансии</span>
                    <strong>{activeVacanciesCount}</strong>
                </article>
            </section>

            <section className="admin-grid">
                <article className="admin-card admin-card-wide" id="admin-requests-card">
                    <div className="admin-card-heading" id="admin-requests-focus">
                        <div>
                            <span>Пользователи</span>
                            <p className="admin-card-subtitle">
                                ADMIN: {adminUsersCount} · CLIENT: {clientUsersCount}
                            </p>
                        </div>
                        <div className="admin-heading-controls">
                            <label className="admin-search">
                                Поиск
                                <input
                                    type="search"
                                    value={userSearch}
                                    onChange={(event) => setUserSearch(event.target.value)}
                                    placeholder="ФИО, телефон, роль, id"
                                />
                            </label>
                            <label>
                                Роль
                                <select value={userRoleFilter}
                                        onChange={(event) => setUserRoleFilter(event.target.value)}>
                                    <option value="">Все</option>
                                    {userRoles.map((role) => (
                                        <option key={role} value={role}>{role}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                Сортировка
                                <select value={userSort} onChange={(event) => setUserSort(event.target.value)}>
                                    {userSortOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="admin-inline-toggle">
                                <input
                                    type="checkbox"
                                    checked={prioritizeFavorites}
                                    onChange={(event) => setPrioritizeFavorites(event.target.checked)}
                                />
                                Избранные первыми
                            </label>
                            {userSort === "requests" && (
                                <label className="admin-request-sort-panel">
                                    Тип сортировки заявок
                                    <select
                                        value={userRequestSort}
                                        onChange={(event) => setUserRequestSort(event.target.value)}
                                    >
                                        {userRequestSortOptions.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <label>
                                Показывать
                                <select
                                    value={userPageSize}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setUserPageSize(value === "all" ? "all" : Number(value));
                                        setVisibleUsersLimit(value === "all" ? visibleUsers.length : Number(value));
                                    }}
                                >
                                    {userPageSizeOptions.map((option) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                    <option value="all">Все</option>
                                </select>
                            </label>
                            {hasUserFilters && (
                                <button
                                    className="admin-ghost-button"
                                    type="button"
                                    onClick={() => {
                                        setUserSearch("");
                                        setUserRoleFilter("");
                                    }}
                                >
                                    Сбросить
                                </button>
                            )}
                        </div>
                    </div>
                    <p className="admin-table-summary">
                        {isUserSearchActive
                            ? `Найдено: ${visibleUsers.length}`
                            : `Показано: ${displayedUsers.length} из ${visibleUsers.length}${hasUserFilters ? " по фильтру" : ""}`}
                    </p>
                    <div className="admin-table-wrap">
                        <table className="admin-table admin-users-table">
                            <thead>
                            <tr>
                                <th>★</th>
                                <th>ID</th>
                                <th>ФИО</th>
                                <th>Телефон</th>
                                <th>Заявки</th>
                                <th>Оценка</th>
                                <th>Роль</th>
                                <th>Управление</th>
                            </tr>
                            </thead>
                            <tbody
                                className={isUsersListAnimating ? "admin-users-tbody admin-users-tbody-animating" : "admin-users-tbody"}>
                            {displayedUsers.map((user) => {
                                const isCurrentUser = user.id === auth?.user?.id;
                                const isFavorite = favoriteUserIds.includes(user.id);
                                const nextRole = user.role === "ADMIN" ? "CLIENT" : "ADMIN";
                                const userRequests = findUserRequests(user.phone);
                                const requestsCount = userRequests.length;
                                const requestMetrics = getUserRequestMetrics(user);

                                return (
                                    <Fragment key={user.id}>
                                        <tr>
                                            <td>
                                                <button
                                                    className={isFavorite ? "admin-star-button admin-star-button-active" : "admin-star-button"}
                                                    type="button"
                                                    aria-label={isFavorite ? "Убрать пользователя из избранного" : "Добавить пользователя в избранное"}
                                                    onClick={() => toggleFavoriteUser(user.id)}
                                                >
                                                    ★
                                                </button>
                                            </td>
                                            <td>
                                                <code className="admin-user-id">{user.id.slice(0, 8)}</code>
                                            </td>
                                            <td>
                                                <div className="admin-user-cell">
                                                    <strong>{user.fullName}</strong>
                                                    {isCurrentUser &&
                                                        <small className="admin-badge admin-badge-current">Это
                                                            вы</small>}
                                                </div>
                                            </td>
                                            <td>{user.phone}</td>
                                            <td>{requestsCount}</td>
                                            <td>{formatRubles(requestMetrics.requestsTotalPrice)}</td>
                                            <td>
                                                <small
                                                    className={user.role === "ADMIN" ? "admin-badge admin-badge-active" : "admin-badge"}>
                                                    {user.role}
                                                </small>
                                            </td>
                                            <td>
                                                <div className="admin-user-actions">
                                                    <select
                                                        value={user.role}
                                                        disabled={isCurrentUser || pendingAction === `user-${user.id}`}
                                                        onChange={(event) => handleRoleChange(user.id, event.target.value)}
                                                    >
                                                        {userRoles.map((role) => (
                                                            <option key={role} value={role}>{role}</option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        className="admin-compact-button"
                                                        type="button"
                                                        disabled={isCurrentUser || pendingAction === `user-${user.id}`}
                                                        onClick={() => handleRoleChange(user.id, nextRole)}
                                                    >
                                                        {nextRole === "ADMIN" ? "Сделать админом" : "Сделать клиентом"}
                                                    </button>
                                                    <button
                                                        className="admin-compact-button"
                                                        type="button"
                                                        onClick={() => setExpandedUserId(expandedUserId === user.id ? "" : user.id)}
                                                    >
                                                        {expandedUserId === user.id ? "Скрыть" : "Детали"}
                                                    </button>
                                                    <button
                                                        className="admin-compact-button"
                                                        type="button"
                                                        disabled={requestsCount === 0}
                                                        onClick={() => handleUserRequestNavigation(user)}
                                                    >
                                                        {requestsCount > 1 ? `Выбрать заявку (${requestsCount})` : "К заявке"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedUserId === user.id && (
                                            <tr className="admin-user-details-row">
                                                <td colSpan="8">
                                                    <div className="admin-user-details">
                                                        <span><strong>ID:</strong> {user.id}</span>
                                                        <span><strong>ФИО:</strong> {user.fullName}</span>
                                                        <span><strong>Телефон:</strong> {user.phone}</span>
                                                        <span><strong>Роль:</strong> {user.role}</span>
                                                        <span><strong>Заявки по телефону:</strong> {requestsCount}</span>
                                                        <span><strong>Сумма заявок:</strong> {formatRubles(requestMetrics.requestsTotalPrice)}</span>
                                                        <span><strong>Самая дорогая заявка:</strong> {formatRubles(requestMetrics.requestsMaxPrice)}</span>
                                                        <span><strong>Последняя заявка:</strong> {formatAdminDate(userRequests[0]?.createdAt)}</span>
                                                        <span className="admin-user-future-field">
                                                                <strong>Марка автомобиля:</strong> не указана
                                                            </span>
                                                    </div>
                                                    {expandedUserRequestsId === user.id && requestsCount > 1 && (
                                                        <div className="admin-user-request-picker">
                                                            <strong>Выберите заявку пользователя:</strong>
                                                            <div>
                                                                {userRequests.map((request) => (
                                                                    <button
                                                                        className="admin-request-pill"
                                                                        key={request.id}
                                                                        type="button"
                                                                        onClick={() => focusRequest(request.id)}
                                                                    >
                                                                        {request.serviceType || "Заявка"} · {request.vehicle || "техника не указана"} · {formatAdminDate(request.createdAt)}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                            {!isLoading && displayedUsers.length === 0 && (
                                <tr>
                                    <td colSpan="8">Пользователи не найдены.</td>
                                </tr>
                            )}
                            {!isLoading && hiddenUsersCount > 0 && (
                                <tr>
                                    <td className="admin-expand-cell" colSpan="8">
                                        <button
                                            className="admin-expand-button"
                                            type="button"
                                            onClick={handleShowMoreUsers}
                                        >
                                            Показать ещё {nextUsersCount}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {!isLoading && userPageSize !== "all" && displayedUsers.length > userPageStep && (
                                <tr>
                                    <td className="admin-expand-cell" colSpan="8">
                                        <button
                                            className="admin-expand-button"
                                            type="button"
                                            onClick={() => setVisibleUsersLimit(userPageStep)}
                                        >
                                            Свернуть список
                                        </button>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </article>

                <article className="admin-card admin-card-wide">
                    <div className="admin-card-heading">
                        <span>Заявки</span>
                        <div className="admin-heading-controls">
                            <label>
                                Статус
                                <select
                                    value={statusFilter}
                                    onChange={(event) => {
                                        pendingRequestFocusRef.current = "";
                                        setFocusedRequestId("");
                                        setStatusFilter(event.target.value);
                                    }}
                                >
                                    <option value="">Все</option>
                                    {requestStatuses.map((status) => (
                                        <option key={status} value={status}>{requestStatusLabels[status]}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="admin-search">
                                Поиск
                                <input
                                    type="search"
                                    value={requestSearch}
                                    onChange={(event) => {
                                        pendingRequestFocusRef.current = "";
                                        setFocusedRequestId("");
                                        setRequestSearch(event.target.value);
                                    }}
                                    placeholder="Клиент, телефон, услуга"
                                />
                            </label>
                            <label className="admin-inline-toggle">
                                <input
                                    type="checkbox"
                                    checked={prioritizePinnedRequests}
                                    onChange={(event) => setPrioritizePinnedRequests(event.target.checked)}
                                />
                                Избранные первыми
                            </label>
                            <label>
                                Показывать
                                <select
                                    value={requestPageSize}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setRequestPageSize(value === "all" ? "all" : Number(value));
                                        setVisibleRequestsLimit(value === "all" ? visibleServiceRequests.length : Number(value));
                                    }}
                                >
                                    {requestPageSizeOptions.map((option) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                    <option value="all">Все</option>
                                </select>
                            </label>
                        </div>
                    </div>
                    <p className="admin-table-summary">
                        {isRequestSearchActive
                            ? `Найдено: ${visibleServiceRequests.length}`
                            : `Показано: ${displayedServiceRequests.length} из ${visibleServiceRequests.length}${hasRequestFilters ? " по фильтру" : ""}`}
                    </p>
                    <div className={isRequestsListAnimating ? "admin-request-cards admin-request-cards-animating" : "admin-request-cards"}>
                        {displayedServiceRequests.map((request) => {
                            const isExpanded = expandedRequestId === request.id;
                            const noteValue = requestNotes[request.id] ?? request.adminNote ?? "";

                            return (
                                <article
                                    className={[
                                        "admin-request-card",
                                        request.pinned ? "admin-request-card-pinned" : "",
                                        request.id === focusedRequestId ? "admin-request-card-focused" : "",
                                    ].filter(Boolean).join(" ")}
                                    key={request.id}
                                    ref={request.id === focusedRequestId ? focusedRequestRowRef : null}
                                    tabIndex={request.id === focusedRequestId ? -1 : undefined}
                                >
                                    <div className="admin-request-card-main">
                                        <button
                                            className={request.pinned ? "admin-star-button admin-star-button-active" : "admin-star-button"}
                                            type="button"
                                            aria-label={request.pinned ? "Открепить заявку" : "Закрепить заявку"}
                                            disabled={pendingAction === `request-fields-${request.id}`}
                                            onClick={() => handleRequestAdminFieldsChange(request, {pinned: !request.pinned})}
                                        >
                                            ★
                                        </button>
                                        <div className="admin-request-card-title">
                                            <strong>{request.customerName}</strong>
                                            <span>{request.serviceType || "Услуга не указана"}</span>
                                        </div>
                                        <span>{request.phone}</span>
                                        <span>{request.vehicle || "Техника не указана"}</span>
                                        <small className={`admin-request-status admin-request-status-${request.status.toLowerCase()}`}>
                                            {requestStatusLabels[request.status] || request.status}
                                        </small>
                                        <div className="admin-request-card-actions">
                                            <select
                                                value={request.status}
                                                disabled={pendingAction === `request-${request.id}`}
                                                onChange={(event) => handleStatusChange(request.id, event.target.value)}
                                            >
                                                {requestStatuses.map((status) => (
                                                    <option key={status} value={status}>{requestStatusLabels[status]}</option>
                                                ))}
                                            </select>
                                            <button
                                                className="admin-compact-button"
                                                type="button"
                                                onClick={() => setExpandedRequestId(isExpanded ? "" : request.id)}
                                            >
                                                {isExpanded ? "Свернуть" : "Подробнее"}
                                            </button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="admin-request-card-details">
                                            <dl>
                                                <div>
                                                    <dt>ID</dt>
                                                    <dd>{request.id}</dd>
                                                </div>
                                                <div>
                                                    <dt>Клиент</dt>
                                                    <dd>{request.customerName || "—"}</dd>
                                                </div>
                                                <div>
                                                    <dt>Телефон</dt>
                                                    <dd>{request.phone || "—"}</dd>
                                                </div>
                                                <div>
                                                    <dt>Услуга</dt>
                                                    <dd>{request.serviceType || "—"}</dd>
                                                </div>
                                                <div>
                                                    <dt>Техника</dt>
                                                    <dd>{request.vehicle || "—"}</dd>
                                                </div>
                                                <div>
                                                    <dt>Статус</dt>
                                                    <dd>{requestStatusLabels[request.status] || request.status || "—"}</dd>
                                                </div>
                                                <div>
                                                    <dt>Закреплена</dt>
                                                    <dd>{request.pinned ? "Да" : "Нет"}</dd>
                                                </div>
                                                <div>
                                                    <dt>Создана</dt>
                                                    <dd>{formatAdminDate(request.createdAt)}</dd>
                                                </div>
                                                <div>
                                                    <dt>Обновлена</dt>
                                                    <dd>{formatAdminDate(request.updatedAt)}</dd>
                                                </div>
                                                <div className="admin-request-detail-wide">
                                                    <dt>Комментарий клиента</dt>
                                                    <dd>{request.message || "—"}</dd>
                                                </div>
                                                <div className="admin-request-detail-wide">
                                                    <dt>Заметка администратора</dt>
                                                    <dd>{request.adminNote || "—"}</dd>
                                                </div>
                                            </dl>
                                            <label className="admin-request-note">
                                                Заметка администратора
                                                <textarea
                                                    value={noteValue}
                                                    maxLength="2000"
                                                    placeholder="Внутренняя заметка, клиент её не видит"
                                                    onChange={(event) => handleRequestNoteChange(request.id, event.target.value)}
                                                />
                                            </label>
                                            <div className="admin-request-card-actions">
                                                <button
                                                    className="admin-compact-button"
                                                    type="button"
                                                    disabled={pendingAction === `request-fields-${request.id}`}
                                                    onClick={() => handleRequestAdminFieldsChange(request, {adminNote: noteValue})}
                                                >
                                                    Сохранить заметку
                                                </button>
                                                <button
                                                    className="admin-compact-button"
                                                    type="button"
                                                    disabled={!noteValue || pendingAction === `request-fields-${request.id}`}
                                                    onClick={() => handleRequestAdminFieldsChange(request, {adminNote: ""})}
                                                >
                                                    Очистить заметку
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                        {!isLoading && visibleServiceRequests.length === 0 && (
                            <p className="admin-empty">Заявки не найдены.</p>
                        )}
                        {!isLoading && hiddenRequestsCount > 0 && (
                            <div className="admin-request-expand-actions">
                                <button
                                    className="admin-expand-button"
                                    type="button"
                                    onClick={handleShowMoreRequests}
                                >
                                    Показать ещё {nextRequestsCount}
                                </button>
                            </div>
                        )}
                        {!isLoading && requestPageSize !== "all" && displayedServiceRequests.length > requestPageStep && (
                            <div className="admin-request-expand-actions">
                                <button
                                    className="admin-expand-button"
                                    type="button"
                                    onClick={() => setVisibleRequestsLimit(requestPageStep)}
                                >
                                    Свернуть список
                                </button>
                            </div>
                        )}
                    </div>
                </article>

                <article className="admin-card">
                    <div className="admin-card-heading">
                        <span>Прайс</span>
                        <strong>{prices.length}</strong>
                    </div>
                    <form className="admin-form" onSubmit={handlePriceSubmit}>
                        <label>
                            Название
                            <input
                                value={priceForm.title}
                                maxLength="120"
                                onChange={(event) => handlePriceFieldChange("title", event.target.value)}
                                required
                            />
                        </label>
                        <label>
                            Цена
                            <input
                                value={priceForm.price}
                                maxLength="80"
                                onChange={(event) => handlePriceFieldChange("price", event.target.value)}
                                required
                            />
                        </label>
                        <label>
                            Описание
                            <textarea
                                value={priceForm.description}
                                maxLength="2000"
                                onChange={(event) => handlePriceFieldChange("description", event.target.value)}
                                required
                            />
                        </label>
                        <div className="admin-form-row">
                            <label>
                                Сортировка
                                <input
                                    type="number"
                                    value={priceForm.sortOrder}
                                    onChange={(event) => handlePriceFieldChange("sortOrder", event.target.value)}
                                />
                                <small className="admin-form-hint">
                                    Чем меньше число, тем выше позиция. При одинаковом значении сортировка идёт по названию.
                                </small>
                            </label>
                            <label className="admin-inline-check">
                                <input
                                    type="checkbox"
                                    checked={priceForm.active}
                                    onChange={(event) => handlePriceFieldChange("active", event.target.checked)}
                                />
                                Активно
                            </label>
                        </div>
                        <div className="admin-form-actions">
                            <button type="submit" disabled={pendingAction === "price-form"}>
                                {editingPriceId ? "Сохранить" : "Добавить"}
                            </button>
                            {editingPriceId && <button type="button" onClick={resetPriceForm}>Отмена</button>}
                        </div>
                    </form>
                    <div className="admin-list">
                        {!isLoading && prices.length === 0 && (
                            <p className="admin-empty">Прайс пока пуст.</p>
                        )}
                        {prices.map((price) => (
                            <div className="admin-list-item" key={price.id}>
                                <strong>{price.title}</strong>
                                <span>{price.price}</span>
                                <small className={price.active ? "admin-badge admin-badge-active" : "admin-badge"}>
                                    {price.active ? "Активно" : "Скрыто"}
                                </small>
                                <div className="admin-list-actions">
                                    <button type="button" onClick={() => startEditPrice(price)}>Править</button>
                                    <button
                                        type="button"
                                        disabled={!price.active || pendingAction === `price-${price.id}`}
                                        onClick={() => handlePriceDeactivate(price.id)}
                                    >
                                        Скрыть
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </article>

                <article className="admin-card">
                    <div className="admin-card-heading">
                        <span>Персонал</span>
                        <strong>{staffMembers.length}</strong>
                    </div>
                    <form className="admin-form" onSubmit={handleStaffSubmit}>
                        <label>
                            Имя
                            <input
                                value={staffForm.name}
                                maxLength="120"
                                onChange={(event) => handleStaffFieldChange("name", event.target.value)}
                                required
                            />
                        </label>
                        <label>
                            Роль
                            <input
                                value={staffForm.role}
                                maxLength="120"
                                onChange={(event) => handleStaffFieldChange("role", event.target.value)}
                                required
                            />
                        </label>
                        <label>
                            Описание
                            <textarea
                                value={staffForm.description}
                                maxLength="2000"
                                onChange={(event) => handleStaffFieldChange("description", event.target.value)}
                                required
                            />
                        </label>
                        <div className="admin-form-row">
                            <label>
                                Сортировка
                                <input
                                    type="number"
                                    value={staffForm.sortOrder}
                                    onChange={(event) => handleStaffFieldChange("sortOrder", event.target.value)}
                                />
                                <small className="admin-form-hint">
                                    Чем меньше число, тем выше сотрудник. При одинаковом значении сортировка идёт по имени.
                                </small>
                            </label>
                            <label className="admin-inline-check">
                                <input
                                    type="checkbox"
                                    checked={staffForm.active}
                                    onChange={(event) => handleStaffFieldChange("active", event.target.checked)}
                                />
                                Активно
                            </label>
                        </div>
                        <div className="admin-form-actions">
                            <button type="submit" disabled={pendingAction === "staff-form"}>
                                {editingStaffId ? "Сохранить" : "Добавить"}
                            </button>
                            {editingStaffId && <button type="button" onClick={resetStaffForm}>Отмена</button>}
                        </div>
                    </form>
                    <div className="admin-list">
                        {!isLoading && staffMembers.length === 0 && (
                            <p className="admin-empty">Список персонала пока пуст.</p>
                        )}
                        {staffMembers.map((member) => (
                            <div className="admin-list-item" key={member.id}>
                                <strong>{member.name}</strong>
                                <span>{member.role}</span>
                                <small className={member.active ? "admin-badge admin-badge-active" : "admin-badge"}>
                                    {member.active ? "Активно" : "Скрыто"}
                                </small>
                                <div className="admin-list-actions">
                                    <button type="button" onClick={() => startEditStaff(member)}>Править</button>
                                    <button
                                        type="button"
                                        disabled={!member.active || pendingAction === `staff-${member.id}`}
                                        onClick={() => handleStaffDeactivate(member.id)}
                                    >
                                        Скрыть
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </article>

                <article className="admin-card">
                    <div className="admin-card-heading">
                        <span>Вакансии</span>
                        <strong>{jobVacancies.length}</strong>
                    </div>
                    <form className="admin-form" onSubmit={handleVacancySubmit}>
                        {vacancyError && (
                            <p className="admin-inline-error">
                                Вакансии временно недоступны: {vacancyError}
                            </p>
                        )}
                        <label>
                            Название
                            <input
                                value={vacancyForm.title}
                                maxLength="120"
                                onChange={(event) => handleVacancyFieldChange("title", event.target.value)}
                                required
                            />
                        </label>
                        <label>
                            Описание
                            <textarea
                                value={vacancyForm.description}
                                maxLength="2000"
                                onChange={(event) => handleVacancyFieldChange("description", event.target.value)}
                                required
                            />
                        </label>
                        <div className="admin-form-row">
                            <label>
                                Сортировка
                                <input
                                    type="number"
                                    value={vacancyForm.sortOrder}
                                    onChange={(event) => handleVacancyFieldChange("sortOrder", event.target.value)}
                                />
                                <small className="admin-form-hint">
                                    Чем меньше число, тем выше вакансия. При одинаковом значении сортировка идёт по названию.
                                </small>
                            </label>
                            <label className="admin-inline-check">
                                <input
                                    type="checkbox"
                                    checked={vacancyForm.active}
                                    onChange={(event) => handleVacancyFieldChange("active", event.target.checked)}
                                />
                                Активно
                            </label>
                        </div>
                        <div className="admin-form-actions">
                            <button type="submit" disabled={pendingAction === "vacancy-form"}>
                                {editingVacancyId ? "Сохранить" : "Добавить"}
                            </button>
                            {editingVacancyId && <button type="button" onClick={resetVacancyForm}>Отмена</button>}
                        </div>
                    </form>
                    <div className="admin-list">
                        {!isLoading && jobVacancies.length === 0 && (
                            <p className="admin-empty">Список вакансий пока пуст.</p>
                        )}
                        {jobVacancies.map((vacancy) => (
                            <div className="admin-list-item" key={vacancy.id}>
                                <strong>{vacancy.title}</strong>
                                <span>{vacancy.description}</span>
                                <small className={vacancy.active ? "admin-badge admin-badge-active" : "admin-badge"}>
                                    {vacancy.active ? "Активно" : "Скрыто"}
                                </small>
                                <div className="admin-list-actions">
                                    <button type="button" onClick={() => startEditVacancy(vacancy)}>Править</button>
                                    <button
                                        type="button"
                                        disabled={pendingAction === `vacancy-${vacancy.id}`}
                                        onClick={() => handleVacancyActiveChange(vacancy, !vacancy.active)}
                                    >
                                        {vacancy.active ? "Скрыть" : "Показать"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </article>
            </section>
        </main>
    );
}

export default Admin;
